import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';
import './MyWorkspaceCard.css';

const PMS_API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/pms` : '/api/pms';

function MyWorkspace() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState('card');
  const [page, setPage] = useState(1);
  const limit = 12;
  const [cardLimit, setCardLimit] = useState(11); // 프로젝트 11개 + 생성카드 1개 = 총 12개 노출

  const [searchType, setSearchType] = useState('project_name');
  const [searchValue, setSearchValue] = useState('');

  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [includeCompleted, setIncludeCompleted] = useState(false);

  const fetchProjects = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${PMS_API_URL}/workspace-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.ok) {
        setProjects(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load projects', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchProjects();
  }, [token, fetchProjects]);

  const filteredProjects = useMemo(() => {
    // 1. 내 프로젝트 1차 필터링 (필수: 나와 연관된 프로젝트만 추출)
    const uId = String(user?.id);
    const uLoginId = String(user?.userid);

    const myProjects = projects.filter(p => {
      // 0. 상태 필터링: 반려, 취소, 검토중, 대기중은 워크스페이스에 표시 안 함
      if (p.status === 'canceled' || p.status === 'rejected' || p.status === 'review' || p.status === 'pending') return false;
      if (!includeCompleted && p.status === 'completed') return false;

      // 관리자 권한이면 모든 프로젝트 통과
      if (user?.role === 'admin') return true;

      // 마스터 3인방 여부
      const isMaster = String(p.author_id) === uId || String(p.author_id) === uLoginId ||
        String(p.leader_id) === uId || String(p.leader_id) === uLoginId ||
        String(p.reviewer_id) === uId || String(p.reviewer_id) === uLoginId;
      if (isMaster) return true;

      // 팀원/참조자 여부 (프로젝트 레벨)
      const isProjectLevelMember = p.members && p.members.some(m =>
        String(m.user_id) === uId ||
        String(m.user_id) === uLoginId ||
        (m.user && String(m.user.id) === uId) ||
        (m.user && String(m.user.userid) === uLoginId)
      );
      if (isProjectLevelMember) return true;

      // 세부항목 담당자/참조자 여부 (태스크 레벨)
      if (p.categories) {
        const isTaskLevelMember = p.categories.some(cat =>
          cat.tasks && cat.tasks.some(t =>
            t.members && t.members.some(m =>
              String(m.user_id) === uId ||
              String(m.user_id) === uLoginId ||
              (m.user && String(m.user.id) === uId) ||
              (m.user && String(m.user.userid) === uLoginId)
            )
          )
        );
        if (isTaskLevelMember) return true;
      }

      return false;
    });
    // 의존성 배열에 includeCompleted 추가

    // 2. 검색어 2차 필터링
    if (!isSearching) return myProjects;
    return myProjects.filter(p => {
      if (searchParams.project_name && !p.title?.includes(searchParams.project_name)) return false;
      if (searchParams.leader && !p.leader?.name?.includes(searchParams.leader)) return false;
      if (searchParams.author && !p.author?.name?.includes(searchParams.author)) return false;
      if (searchParams.department && !(p.leader?.org?.dept_name?.includes(searchParams.department) || p.author?.org?.dept_name?.includes(searchParams.department))) return false;

      if (searchParams.startDate && searchParams.endDate) {
        const targetDateStr = p.start_date || p.created_at;
        if (targetDateStr) {
          const targetDate = new Date(targetDateStr.substring(0, 10));
          const sDate = new Date(searchParams.startDate);
          const eDate = new Date(searchParams.endDate);
          if (targetDate < sDate || targetDate > eDate) return false;
        }
      }
      return true;
    });
  }, [projects, user, isSearching, searchParams, includeCompleted]);

  const total = filteredProjects.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const listData = filteredProjects.slice((page - 1) * limit, page * limit);
  const cardData = filteredProjects.slice(0, cardLimit);

  const handleStartProject = async (id) => {
    if (window.confirm("프로젝트를 공식적으로 시작합니다. 상태를 진행 중으로 변경하시겠습니까?")) {
      try {
        const res = await axios.put(`${PMS_API_URL}/projects/${id}/status`,
          { status: 'in_progress', review_opinion: '' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.ok) {
          fetchProjects(); // 목록 갱신
        } else {
          alert(res.data.message || '상태 변경 중 오류가 발생했습니다.');
        }
      } catch (err) {
        console.error(err);
        alert('서버 오류가 발생했습니다.');
      }
    }
  };

  const handleCompleteProject = async (proj) => {
    const isReviewer = String(proj.reviewer_id) === String(user?.id);
    const hasReviewer = !!proj.reviewer_id;
    const isAlreadyCompletedOnce = !!proj.completed_date;

    if (hasReviewer && !isReviewer && !isAlreadyCompletedOnce) {
      if (window.confirm("이 프로젝트는 최초 완료 시 검토자의 승인이 필요합니다. 검토 요청(결재 상신)하시겠습니까?")) {
        try {
          const res = await axios.put(`${PMS_API_URL}/projects/${proj.id}/status`,
            { status: 'review', review_opinion: '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.ok) {
            fetchProjects();
          } else {
            alert(res.data.message || '상태 변경 중 오류가 발생했습니다.');
          }
        } catch (err) {
          console.error(err);
          if (err.response?.data?.message) {
            alert(err.response.data.message);
          } else {
            alert('서버 오류가 발생했습니다.');
          }
        }
      }
    } else {
      if (window.confirm("프로젝트를 완료 처리하시겠습니까?")) {
        try {
          const res = await axios.put(`${PMS_API_URL}/projects/${proj.id}/status`,
            { status: 'completed', review_opinion: '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.ok) {
            fetchProjects();
          } else {
            alert(res.data.message || '상태 변경 중 오류가 발생했습니다.');
          }
        } catch (err) {
          console.error(err);
          if (err.response?.data?.message) {
            alert(err.response.data.message);
          } else {
            alert('서버 오류가 발생했습니다.');
          }
        }
      }
    }
  };

  const handleSearch = () => {
    if (!searchValue.trim()) {
      alert('검색어가 지정되지 않았습니다.');
      return;
    }

    const params = {};
    if (searchType === 'project_name') params.project_name = searchValue;
    if (searchType === 'author') params.author = searchValue;
    if (searchType === 'leader') params.leader = searchValue;
    if (searchType === 'member') params.member = searchValue;
    setSearchParams(params);
    setIsSearching(true);
    setPage(1);
    setCardLimit(11);
  };

  const handleReset = () => {
    setIsSearching(false);
    setSearchValue('');
    setSearchParams({});
    setPage(1);
    setCardLimit(11);
  };

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchValue('');
  };

  const formatCurrency = (val) => {
    if (!val) return '0원';
    return Number(val).toLocaleString('ko-KR') + '원';
  };

  return (
    <div className="container-fluid p-3 p-md-4 rounded-4" style={{ backgroundColor: '#f4f5f7', minHeight: 800, border: '1px solid #ced4da' }}>

      {/* 상단 헤더 컨테이너 */}
      <div className="card shadow-sm rounded-4 mb-2" style={{ backgroundColor: '#ffffff', border: '1px solid #ced4da' }}>
        <div className="card-body d-flex justify-content-between align-items-center py-3 px-3">
          <div className="d-flex align-items-center">
            <div className="bg-primary text-white rounded-circle d-flex justify-content-center align-items-center me-3 shadow-sm" style={{ width: '48px', height: '48px' }}>
              <i className="fas fa-layer-group fs-5"></i>
            </div>
            <div>
              <div className="d-flex align-items-center mb-1">
                <h4 className="mb-0 fw-bold text-dark d-flex align-items-center">
                  내 워크스페이스
                  <span className="badge bg-light text-primary border border-primary rounded-pill ms-2 fw-bold shadow-sm" style={{ fontSize: '0.75rem', padding: '0.35em 0.65em', transform: 'translateY(-1px)' }}>
                    {total}건
                  </span>
                </h4>
                {isSearching && (
                  <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.8rem' }}>
                    (검색결과)
                  </span>
                )}
              </div>
              <span className="text-muted fw-medium" style={{ fontSize: '0.85rem' }}>나에게 배정된 프로젝트를 한눈에 관리하세요.</span>
            </div>
          </div>

          <div>
            <style>{`
              .refresh-btn:hover .fa-sync-alt { transform: rotate(180deg); }
              .refresh-btn .fa-sync-alt { transition: transform 0.3s ease-in-out; }
            `}</style>
            <button className="btn btn-light fw-bold text-dark btn-sm shadow-sm border refresh-btn" onClick={fetchProjects}>
              <i className="fas fa-sync-alt me-1"></i>새로고침
            </button>
          </div>
        </div>
      </div>

      {/* 검색 영역 컨테이너 */}
      <div className="card mb-3 shadow-sm rounded-4" style={{ backgroundColor: '#ffffff', border: '1px solid #ced4da' }}>
        <div className="card-body py-2 px-3" style={{ paddingTop: "10px", paddingBottom: "10px" }}>
          <div className="row g-2 align-items-center">
            <div className="col-auto">
              <select className="form-select text-dark border-secondary" style={{ width: '150px' }} value={searchType} onChange={handleSearchTypeChange}>
                <option value="project_name">프로젝트명</option>
                <option value="author">요청자</option>
                <option value="leader">책임자(PM)</option>
                <option value="member">담당자</option>
              </select>
            </div>

            <div className="col-auto" style={{ width: '300px' }}>
              <input
                type="text"
                className="form-control border-secondary"
                placeholder="검색어 입력"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            <div className="col-auto">
              <button className="btn btn-secondary px-3" onClick={handleSearch} style={{ backgroundColor: '#6c8496', border: 'none' }}>
                검색
              </button>
            </div>
            <div className="col-auto">
              <button className="btn btn-outline-secondary px-3" onClick={handleReset}>
                초기화
              </button>
            </div>
            {/* 카드/리스트 뷰 토글 버튼을 검색창 우측으로 이동 */}
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch m-0 p-0 d-flex align-items-center" style={{ minHeight: 'auto' }}>
                  <input className="form-check-input m-0 mt-1 float-none" type="checkbox" id="includeCompletedSwitch" style={{ cursor: 'pointer', width: '34px', height: '18px' }} checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} />
                </div>
                <label className="fw-bold text-secondary mb-0 mt-1" htmlFor="includeCompletedSwitch" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>완료 포함</label>
              </div>
              <div className="bg-light p-1 rounded d-flex border shadow-sm">
                <button
                  className={`btn btn-sm rounded px-3 py-1 fw-bold ${viewMode === 'card' ? 'bg-primary text-white shadow-sm' : 'bg-transparent text-secondary border-0'}`}
                  style={{ fontSize: '0.8rem' }}
                  onClick={() => setViewMode('card')}
                >
                  <i className="fas fa-th-large me-1"></i>카드
                </button>
                <button
                  className={`btn btn-sm rounded px-3 py-1 fw-bold ${viewMode === 'list' ? 'bg-primary text-white shadow-sm' : 'bg-transparent text-secondary border-0'}`}
                  style={{ fontSize: '0.8rem' }}
                  onClick={() => setViewMode('list')}
                >
                  <i className="fas fa-list me-1"></i>리스트
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 로딩 */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : (
        <>
          {viewMode === 'card' ? (
            /* 카드 뷰 */
            <div className="row g-3">
              {cardData.map(proj => {
                const isLeaderOrAuthor = user?.role === 'admin' ||
                  String(proj.author_id) === String(user?.id) || String(proj.author_id) === String(user?.userid) ||
                  String(proj.leader_id) === String(user?.id) || String(proj.leader_id) === String(user?.userid);
                  
                const isCompleted = proj.status === 'completed';

                const todayStr = new Date().toISOString().split('T')[0];
                const isDelayed = !isCompleted && proj.end_date && proj.end_date < todayStr;

                let badgeText = '진행중';
                let badgeClass = 'bg-primary';
                let progressClass = 'bg-primary';
                let textClass = 'text-primary';

                if (isCompleted) {
                  badgeText = '완료';
                  badgeClass = 'bg-success';
                  progressClass = 'bg-success';
                  textClass = 'text-success';
                } else if (isDelayed) {
                  badgeText = (proj.status === 'ready' || proj.status === 'pending') ? '시작 지연' : '지연';
                  badgeClass = 'bg-danger';
                  progressClass = 'bg-danger';
                  textClass = 'text-danger';
                } else if (proj.status === 'ready') {
                  badgeText = '시작 전';
                  badgeClass = 'bg-warning text-dark';
                }

                const pmText = `${proj.leader?.name || '미지정'} / ${proj.leader?.org?.position || ''}(${proj.leader?.org?.dept_name || ''})`;

                return (
                  <div className="col-xxl-3 col-xl-4 col-md-6 col-12" key={proj.id}>
                    <div className="card shadow-sm border-0 h-100 project-card position-relative overflow-hidden"
                      style={{ cursor: 'pointer', transition: 'all 0.2s', borderRadius: '8px' }}
                      onMouseEnter={(e) => {
                        const arrow = e.currentTarget.querySelector('.card-arrow-black');
                        if (arrow) {
                          arrow.style.opacity = '1';
                          arrow.style.transform = 'translateY(0)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const arrow = e.currentTarget.querySelector('.card-arrow-black');
                        if (arrow) {
                          arrow.style.opacity = '0';
                          arrow.style.transform = 'translateY(10px)';
                        }
                      }}
                      onClick={() => navigate(`/pms/project-rooms/${proj.id}`)}>

                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className={`badge ${badgeClass}`}>
                            {badgeText}
                          </span>
                          <small className="text-muted fw-medium" style={{ fontSize: '0.8rem' }}>
                            <i className="far fa-calendar-alt me-1"></i>
                            {proj.start_date ? proj.start_date.substring(0, 10) : ''} ~ {proj.end_date ? proj.end_date.substring(0, 10) : ''}
                          </small>
                        </div>

                        <h5 className="card-title fw-bold text-dark mb-3 text-truncate" title={proj.title}>
                          {proj.title}
                        </h5>

                        {/* PM 정보 ... 처리 (text-truncate 적용, 배지가 찌그러지지 않게 flex-shrink-0) */}
                        <div className="mb-2 d-flex align-items-center w-100">
                          <span className="badge bg-secondary me-2 flex-shrink-0" style={{ fontSize: '0.7rem', color: '#fff' }}>책임자(PM)</span>
                          <span className="text-dark fw-bold text-truncate" style={{ fontSize: '0.85rem' }} title={pmText}>
                            {pmText}
                          </span>
                        </div>

                        <div className="d-flex align-items-center">
                          <div className="progress flex-grow-1 me-2 bg-light" style={{ height: '4px', borderRadius: '2px' }}>
                            <div className={`progress-bar ${progressClass}`} style={{ width: `${proj.progress_rate || 0}%`, borderRadius: '2px' }}></div>
                          </div>
                          <span className={`${textClass} fw-bold`} style={{ fontSize: '0.8rem', minWidth: '32px' }}>{proj.progress_rate || 0}%</span>
                        </div>

                        <div className="position-absolute d-flex justify-content-between align-items-center w-100 px-3"
                          style={{ bottom: '10px', left: '0' }}>
                          <small className="text-secondary fw-bold" style={{ fontSize: '0.75rem' }}>
                            <i className="fas fa-pencil-alt me-1"></i>작성일: {proj.created_at ? proj.created_at.substring(0, 16).replace('T', ' ') : ''}
                          </small>
                          <div className="d-flex align-items-center">
                            {isLeaderOrAuthor && proj.status === 'ready' ? (
                              <button
                                className="btn btn-sm btn-outline-primary fw-bold px-2 py-0"
                                style={{ fontSize: '0.75rem' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartProject(proj.id);
                                }}
                              >
                                <i className="fas fa-play me-1"></i>진행처리
                              </button>
                            ) : isLeaderOrAuthor && proj.status === 'in_progress' && proj.progress_rate === 100 ? (
                              <button
                                className="btn btn-sm btn-outline-success fw-bold px-2 py-0"
                                style={{ fontSize: '0.75rem' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompleteProject(proj);
                                }}
                              >
                                <i className={proj.reviewer_id && String(proj.reviewer_id) !== String(user?.id) && !proj.completed_date ? "fas fa-paper-plane me-1" : "fas fa-flag-checkered me-1"}></i>
                                {proj.reviewer_id && String(proj.reviewer_id) !== String(user?.id) && !proj.completed_date ? '검토요청' : '완료처리'}
                              </button>
                            ) : (
                              <div className="card-arrow-black" style={{
                                opacity: 0,
                                transform: 'translateY(10px)',
                                transition: 'all 0.2s ease-in-out'
                              }}>
                                <i className="fas fa-arrow-right text-dark" style={{ fontSize: '0.9rem' }}></i>
                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}

              {/* 새 프로젝트 생성 카드 */}
              <div className="col-xxl-3 col-xl-4 col-md-6 col-12">
                <div className="card h-100 shadow-sm border-0 position-relative overflow-hidden"
                  style={{
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    borderRadius: '8px'
                  }}
                  onMouseEnter={(e) => {
                    const hoverText = e.currentTarget.querySelector('.hover-text-new');
                    if (hoverText) {
                      hoverText.style.opacity = '1';
                      hoverText.style.transform = 'translateY(0)';
                      hoverText.style.height = '20px';
                      hoverText.style.marginTop = '8px';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const hoverText = e.currentTarget.querySelector('.hover-text-new');
                    if (hoverText) {
                      hoverText.style.opacity = '0';
                      hoverText.style.transform = 'translateY(5px)';
                      hoverText.style.height = '0px';
                      hoverText.style.marginTop = '0px';
                    }
                  }}
                  onClick={() => navigate('/pms/projects/new')}>
                  <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-3">
                    <div className="d-flex justify-content-center align-items-center mb-3"
                      style={{ width: '48px', height: '48px', backgroundColor: '#eef2f6', borderRadius: '50%' }}>
                      <i className="fas fa-plus" style={{ color: '#a0aec0', fontSize: '1.5rem' }}></i>
                    </div>
                    <h5 className="fw-bold mb-0 text-dark" style={{ fontSize: '1rem' }}>새 프로젝트 생성</h5>

                    {/* 마우스 호버 시 나타나는 텍스트 */}
                    <div className="hover-text-new text-primary fw-bold"
                      style={{
                        opacity: 0,
                        height: '0px',
                        marginTop: '0px',
                        transform: 'translateY(5px)',
                        transition: 'all 0.2s ease-in-out',
                        fontSize: '0.85rem'
                      }}>
                      클릭하여 추가
                    </div>
                  </div>
                </div>
              </div>

              {total > cardLimit && (
                <div className="col-12 d-flex justify-content-center mt-4 mb-3">
                  <button
                    className="btn rounded-pill shadow-sm px-4 py-2 d-flex align-items-center text-white"
                    style={{ backgroundColor: '#6c8496', transition: 'all 0.2s ease-in-out', border: 'none' }}
                    onClick={() => setCardLimit(prev => prev + 12)}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.classList.replace('shadow-sm', 'shadow'); }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.classList.replace('shadow', 'shadow-sm'); }}
                  >
                    <span className="fw-bold me-2" style={{ fontSize: '0.9rem' }}>
                      더보기 <span className="ms-1" style={{ color: '#dcf0fa' }}>({total - cardLimit}개)</span>
                    </span>
                    <i className="fas fa-chevron-down" style={{ color: '#dcf0fa' }}></i>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* 리스트 뷰 */
            <div className="table-responsive p-0 shadow-sm border-0" style={{ backgroundColor: '#fff' }}>
              <table className="table table-hover align-middle mb-0" style={{ minWidth: '1050px', tableLayout: 'fixed' }}>
                <thead className="table-light">
                  <tr style={{ height: '40px' }}>
                    <th className="text-center text-dark fw-bold align-middle" style={{ width: '90px', borderBottom: '2px solid #ced4da' }}>상태</th>
                    <th className="text-dark fw-bold align-middle" style={{ width: 'auto', borderBottom: '2px solid #ced4da' }}>프로젝트 명</th>
                    <th className="text-center text-dark fw-bold align-middle" style={{ width: '200px', borderBottom: '2px solid #ced4da' }}>담당자</th>
                    <th className="text-center text-dark fw-bold align-middle" style={{ width: '180px', borderBottom: '2px solid #ced4da' }}>진척도(%)</th>
                    <th className="text-center text-dark fw-bold align-middle" style={{ width: '140px', borderBottom: '2px solid #ced4da' }}>배정 예산</th>
                    <th className="text-center text-dark fw-bold align-middle" style={{ width: '200px', borderBottom: '2px solid #ced4da' }}>기간</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {listData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        <i className="fas fa-box-open mb-3" style={{ fontSize: '3rem', color: '#e8edf1' }}></i>
                        <p className="mb-0 fs-6 text-secondary">현재 해당하는 프로젝트가 없습니다.</p>
                      </td>
                    </tr>
                  ) : (
                    listData.map(item => {
                      const pmText = `${item.leader?.name || '미지정'} / ${item.leader?.org?.position || ''}(${item.leader?.org?.dept_name || ''})`;
                      return (
                        <tr key={item.id} onClick={() => navigate(`/pms/project-rooms/${item.id}`)} style={{ cursor: 'pointer', transition: 'all 0.2s ease-in-out' }}>
                          <td className="text-center py-3">
                            <span className={`badge rounded-pill px-3 py-2 ${item.status === 'completed' ? 'bg-success' : item.status === 'ready' ? 'bg-warning text-dark' : 'bg-primary'}`} style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                              {item.status === 'completed' ? '완료' : item.status === 'ready' ? '시작 전' : '진행중'}
                            </span>
                          </td>
                          <td className="fw-bold text-dark fs-6 py-3">
                            <div className="text-truncate" title={item.title}>
                              {item.title}
                            </div>
                          </td>
                          <td className="text-center py-3">
                            <div className="badge bg-light text-dark border px-2 py-1 text-truncate mx-auto" style={{ fontSize: '0.75rem', maxWidth: '100%' }} title={pmText}>
                              <i className="fas fa-user me-1 text-secondary"></i>{pmText}
                            </div>
                          </td>
                          <td className="text-center align-middle py-3">
                            <div className="d-flex align-items-center justify-content-center">
                              <span className="me-2 fw-bolder text-dark" style={{ width: '30px', fontSize: '0.8rem' }}>{item.progress_rate || 0}%</span>
                              <div className="progress flex-grow-1 bg-light" style={{ height: '4px', maxWidth: '80px', borderRadius: '2px' }}>
                                <div className={`progress-bar ${item.progress_rate === 100 ? 'bg-success' : 'bg-primary'}`} style={{ width: `${item.progress_rate || 0}%`, borderRadius: '2px' }}></div>
                              </div>
                            </div>
                          </td>
                          <td className="text-center text-dark fw-bold py-3" style={{ fontSize: '0.8rem' }}>{formatCurrency(item.budget)}</td>
                          <td className="text-center text-danger fw-bold py-3" style={{ fontSize: '0.8rem' }}>
                            {item.start_date ? item.start_date.substring(0, 10) : '-'} ~ {item.end_date ? item.end_date.substring(0, 10) : '-'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* 리스트 뷰 전용 페이징 */}
          {viewMode === 'list' && total > 0 && (
            <div className="position-relative d-flex justify-content-center align-items-center mt-3 mb-3 p-2">
              <div className="position-absolute" style={{ left: '10px' }}>
                <span className="badge rounded text-white" style={{ backgroundColor: '#777b8c', fontSize: '13px', padding: '0.25rem 0.6rem' }}>
                  {page} / {totalPages} (총 : {total})
                </span>
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link text-secondary" onClick={() => setPage(p => Math.max(1, p - 1))}>« 이전</button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link text-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>다음 »</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MyWorkspace;
